<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import { upsertAdminEvent, type ManagedEventConfig } from '../../lib/convexApi';
  import EventTicketingEditor from './EventTicketingEditor.svelte';
  import MarkdownView from '../ui/MarkdownView.svelte';
  import Notice from '../ui/Notice.svelte';

  export let communityId: string;
  /** null = create a new event */
  export let event: ManagedEventConfig | null = null;

  const dispatch = createEventDispatcher<{ saved: { eventId: string }; cancel: void; refresh: void }>();

  interface ExtraRow {
    key: number;
    sku: string;
    name: string;
    description: string;
    price: string;
    capacity: string;
    maxPerOrder: string;
  }

  let rowKey = 0;

  let id = event?.id ?? '';
  let idTouched = Boolean(event);
  let name = event?.name ?? '';
  let headline = event?.headline ?? '';
  let description = event?.description ?? '';
  let descriptionPreview = false;
  let flyerUrl = event?.flyerUrl ?? '';
  let locationName = event?.location?.name ?? '';
  let locationAddress = event?.location?.address ?? '';
  let locationMapUrl = event?.location?.mapUrl ?? '';
  let whatsappGroupUrl = event?.whatsappGroupUrl ?? '';
  let applicationUrl = event?.applicationUrl ?? '';
  let scheduleStart = toInputValue(event?.schedule?.start);
  let scheduleEnd = toInputValue(event?.schedule?.end);
  let productName = event?.ticket?.productName ?? 'Ticket';
  let currency = event?.ticket?.currency ?? 'eur';
  let startPrice = String(event?.ticket?.startPrice ?? 0);
  let endPrice = String(event?.ticket?.endPrice ?? event?.ticket?.startPrice ?? 0);
  let dynamicStart = toInputValue(event?.ticket?.dynamicPricing?.start ?? null);
  let dynamicEnd = toInputValue(event?.ticket?.dynamicPricing?.end ?? null);
  let allowInvites = event?.ticket?.allowInvites ?? false;
  let defaultInviteAllowance = event?.ticket?.defaultInviteAllowance != null ? String(event.ticket.defaultInviteAllowance) : '';
  let preCheckoutQuestion = event?.ticket?.preCheckoutQuestion ?? '';
  let extras: ExtraRow[] = (event?.extras ?? []).map(extra => ({
    key: rowKey++,
    sku: extra.sku,
    name: extra.name,
    description: extra.description ?? '',
    price: String(extra.price),
    capacity: extra.capacity === -1 ? '' : String(extra.capacity),
    maxPerOrder: String(extra.maxPerOrder)
  }));

  let saving = false;
  let error: string | null = null;
  /** Set after creating a new event — switches the panel to the ticket-setup step. */
  let createdEventId: string | null = null;

  $: ticketingStub = createdEventId ? { id: createdEventId, ticketing: null } : null;

  function toInputValue(value: string | null | undefined): string {
    if (!value) return '';
    return value.length > 16 ? value.slice(0, 16) : value;
  }

  function slugify(value: string): string {
    return value
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 48);
  }

  function onNameInput() {
    if (!idTouched) {
      id = slugify(name);
    }
  }

  function onIdInput() {
    idTouched = true;
  }

  function addExtra() {
    extras = [...extras, { key: rowKey++, sku: '', name: '', description: '', price: '0', capacity: '', maxPerOrder: '1' }];
  }

  function removeExtra(key: number) {
    extras = extras.filter(row => row.key !== key);
  }

  function parseNumber(raw: string, label: string, { min = 0 }: { min?: number } = {}): number {
    const value = Number(raw);
    if (!Number.isFinite(value) || value < min) {
      throw new Error(`${label} must be a number ≥ ${min}`);
    }
    return value;
  }

  async function save() {
    error = null;
    try {
      const trimmedId = id.trim();
      if (!trimmedId) throw new Error('Event id is required — it becomes part of ticket links.');
      if (!name.trim()) throw new Error('Event name is required.');
      if (!scheduleStart || !scheduleEnd) throw new Error('Start and end times are required.');
      if (Date.parse(scheduleStart) >= Date.parse(scheduleEnd)) {
        throw new Error('The event must start before it ends.');
      }

      const parsedStart = parseNumber(startPrice, 'Start price');
      const parsedEnd = endPrice.trim() === '' ? parsedStart : parseNumber(endPrice, 'End price');

      const parsedExtras = extras.map((row, index) => {
        const label = `Extra ${index + 1}`;
        const sku = row.sku.trim();
        const extraName = row.name.trim();
        if (!sku) throw new Error(`${label}: SKU is required`);
        if (sku.includes(':')) throw new Error(`${label}: SKU must not contain ':'`);
        if (!extraName) throw new Error(`${label}: name is required`);
        return {
          sku,
          name: extraName,
          description: row.description.trim() || null,
          price: parseNumber(row.price, `${label} price`),
          currency,
          capacity: row.capacity.trim() === '' ? -1 : parseNumber(row.capacity, `${label} capacity`, { min: 1 }),
          maxPerOrder: parseNumber(row.maxPerOrder || '1', `${label} max per order`, { min: 1 })
        };
      });
      const skus = parsedExtras.map(extra => extra.sku);
      if (new Set(skus).size !== skus.length) {
        throw new Error('Extras must have unique SKUs.');
      }

      saving = true;
      await upsertAdminEvent(
        {
          id: trimmedId,
          name: name.trim(),
          headline: headline.trim(),
          description: description.trim() || null,
          flyerUrl: flyerUrl.trim() || null,
          whatsappGroupUrl: whatsappGroupUrl.trim() || null,
          applicationUrl: applicationUrl.trim() || null,
          location: {
            name: locationName.trim(),
            address: locationAddress.trim() || null,
            mapUrl: locationMapUrl.trim() || null
          },
          schedule: { start: scheduleStart, end: scheduleEnd },
          ticket: {
            productName: productName.trim() || 'Ticket',
            currency,
            startPrice: parsedStart,
            endPrice: parsedEnd,
            dynamicPricing: {
              start: dynamicStart || null,
              end: dynamicEnd || null
            },
            allowInvites,
            defaultInviteAllowance:
              defaultInviteAllowance.trim() === ''
                ? undefined
                : parseNumber(defaultInviteAllowance, 'Default plus-one allowance', { min: 1 }),
            preCheckoutQuestion: preCheckoutQuestion.trim() || null
          },
          extras: parsedExtras
        },
        event ? undefined : communityId
      );
      if (event) {
        dispatch('saved', { eventId: trimmedId });
      } else {
        // New event: stay open and move to the ticket-setup step.
        createdEventId = trimmedId;
        dispatch('refresh');
      }
    } catch (err) {
      error = err instanceof Error ? err.message : 'Could not save the event.';
    } finally {
      saving = false;
    }
  }
</script>

<div class="editor surface surface--sunken">
  <header class="editor__header">
    <h3>
      {#if createdEventId}
        Tickets · {name}
      {:else if event}
        Edit · {event.name}
      {:else}
        Create event
      {/if}
    </h3>
    <button type="button" class="ghost" on:click={() => dispatch('cancel')}>Close</button>
  </header>

  {#if createdEventId && ticketingStub}
    <Notice variant="success">
      Event created. Set up the tickets — add tiers with their own capacity, price and sale
      window, or keep the single default ticket.
    </Notice>
    <EventTicketingEditor event={ticketingStub} on:changed={() => dispatch('refresh')} />
    <footer class="editor__footer">
      <button type="button" class="accent" on:click={() => dispatch('saved', { eventId: createdEventId ?? '' })}>
        Done
      </button>
    </footer>
  {:else}
  <form class="editor__form" on:submit|preventDefault={save}>
  {#if error}
    <Notice variant="error">{error}</Notice>
  {/if}

  <div class="editor__grid">
    <label>
      Name
      <input type="text" bind:value={name} on:input={onNameInput} required />
    </label>
    <label>
      Event id {event ? '(fixed)' : '(in ticket links)'}
      <input type="text" bind:value={id} on:input={onIdInput} disabled={Boolean(event)} required />
    </label>
    <label class="editor__wide">
      Headline
      <input type="text" bind:value={headline} placeholder="One line shown on the event card" />
    </label>
    <div class="editor__wide editor__desc">
      <div class="editor__desc-head">
        <span>Description</span>
        <div class="editor__desc-tabs" role="tablist" aria-label="Description mode">
          <button
            type="button"
            class="ghost editor__desc-tab"
            class:editor__desc-tab--active={!descriptionPreview}
            on:click={() => (descriptionPreview = false)}
          >
            Write
          </button>
          <button
            type="button"
            class="ghost editor__desc-tab"
            class:editor__desc-tab--active={descriptionPreview}
            on:click={() => (descriptionPreview = true)}
          >
            Preview
          </button>
        </div>
      </div>
      {#if descriptionPreview}
        <div class="editor__desc-preview">
          {#if description.trim()}
            <MarkdownView source={description} />
          {:else}
            <p class="editor__desc-empty">Nothing to preview yet.</p>
          {/if}
        </div>
      {:else}
        <textarea
          rows="6"
          bind:value={description}
          maxlength="5000"
          placeholder="Markdown: # headings, **bold**, [links](https://…), ![images](https://…), - lists"
        ></textarea>
      {/if}
      <span class="editor__desc-hint">
        Markdown only — no HTML. Images: link a picture you host yourself (Imgur, your site, a CDN) with
        ![alt](https://…); Gravity doesn't store images.
      </span>
    </div>
    <div class="editor__wide editor__flyer">
      <label>
        Flyer image URL
        <input
          type="url"
          bind:value={flyerUrl}
          placeholder="https://… (an image you host — 2:1 landscape works best)"
        />
      </label>
      {#if flyerUrl.trim()}
        <img class="editor__flyer-preview" src={flyerUrl.trim()} alt="Flyer preview" />
      {/if}
    </div>
    <label>
      Starts
      <input type="datetime-local" bind:value={scheduleStart} required />
    </label>
    <label>
      Ends
      <input type="datetime-local" bind:value={scheduleEnd} required />
    </label>
    <label>
      Venue name
      <input type="text" bind:value={locationName} />
    </label>
    <label>
      Address
      <input type="text" bind:value={locationAddress} />
    </label>
    <label>
      Map link
      <input type="url" bind:value={locationMapUrl} placeholder="https://maps…" />
    </label>
    <label>
      WhatsApp group link
      <input type="url" bind:value={whatsappGroupUrl} placeholder="https://chat.whatsapp.com/…" />
    </label>
    <label>
      External application URL
      <input type="url" bind:value={applicationUrl} placeholder="Only for external forms" />
    </label>
  </div>

  <details class="editor__section" open={!event}>
    <summary>Base ticket & pricing</summary>
    <p class="editor__hint">
      This is the fallback single ticket. Once you define ticket tiers
      {event ? 'below' : '(next step, after creating)'}, tiers take over pricing and this
      section only names the product.
    </p>
    <div class="editor__grid">
      <label>
        Product name
        <input type="text" bind:value={productName} />
      </label>
      <label>
        Currency
        <select bind:value={currency}>
          <option value="eur">EUR</option>
          <option value="usd">USD</option>
          <option value="gbp">GBP</option>
          <option value="chf">CHF</option>
        </select>
      </label>
      <label>
        Start price
        <input type="number" min="0" step="0.01" bind:value={startPrice} />
      </label>
      <label>
        End price (dynamic rise)
        <input type="number" min="0" step="0.01" bind:value={endPrice} placeholder="same as start" />
      </label>
      <label>
        Price rise begins
        <input type="datetime-local" bind:value={dynamicStart} />
      </label>
      <label>
        Price rise ends
        <input type="datetime-local" bind:value={dynamicEnd} />
      </label>
      <label class="editor__check">
        <input type="checkbox" bind:checked={allowInvites} />
        Guests may bring plus-ones
      </label>
      <label>
        Default plus-one allowance
        <input type="number" min="1" bind:value={defaultInviteAllowance} placeholder="community default" />
      </label>
      <label class="editor__wide">
        Pre-checkout question (optional)
        <textarea rows="2" bind:value={preCheckoutQuestion} maxlength="2500"></textarea>
      </label>
    </div>
  </details>

  <details class="editor__section" open={extras.length > 0}>
    <summary>Extras ({extras.length})</summary>
    <div class="editor__extras">
      {#each extras as row (row.key)}
        <div class="editor__extra surface">
          <div class="editor__grid editor__grid--extras">
            <label>
              SKU
              <input type="text" bind:value={row.sku} placeholder="afterparty" />
            </label>
            <label>
              Name
              <input type="text" bind:value={row.name} />
            </label>
            <label>
              Price
              <input type="number" min="0" step="0.01" bind:value={row.price} />
            </label>
            <label>
              Capacity
              <input type="number" min="1" bind:value={row.capacity} placeholder="unlimited" />
            </label>
            <label>
              Max per order
              <input type="number" min="1" bind:value={row.maxPerOrder} />
            </label>
            <label class="editor__wide">
              Description
              <input type="text" bind:value={row.description} />
            </label>
          </div>
          <button type="button" class="ghost editor__remove" on:click={() => removeExtra(row.key)}>
            Remove extra
          </button>
        </div>
      {/each}
      <button type="button" class="secondary" on:click={addExtra}>Add extra</button>
    </div>
  </details>

  <footer class="editor__footer">
    <button type="submit" class="accent" disabled={saving}>
      {saving ? 'Saving…' : event ? 'Save changes' : 'Create event'}
    </button>
    <button type="button" class="secondary" on:click={() => dispatch('cancel')} disabled={saving}>
      Cancel
    </button>
  </footer>
  </form>

  {#if event}
    <details class="editor__section" open>
      <summary>Tickets, capacity & sale windows</summary>
      <p class="editor__hint">Tickets save separately from the event details above.</p>
      <EventTicketingEditor {event} on:changed={() => dispatch('refresh')} />
    </details>
  {/if}
  {/if}
</div>

<style>
  .editor {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
    padding: var(--space-5);
    border-radius: var(--radius-lg);
  }

  .editor__form {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
  }

  .editor__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--space-4);
  }

  .editor__header h3 {
    margin: 0;
    font-size: var(--font-size-lg);
  }

  .editor__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: var(--space-3) var(--space-4);
  }

  .editor__grid--extras {
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  }

  .editor__wide {
    grid-column: 1 / -1;
  }

  .editor__desc {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
  }

  .editor__flyer {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  .editor__flyer-preview {
    max-height: 120px;
    width: auto;
    max-width: 100%;
    object-fit: cover;
    border: var(--border-subtle);
    align-self: flex-start;
  }

  .editor__desc-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-3);
    font-size: var(--font-size-xs);
    color: var(--color-text-muted);
  }

  .editor__desc-tabs {
    display: flex;
    gap: 0;
  }

  .editor__desc-tab {
    width: auto;
    padding: var(--space-1) var(--space-3);
    font-size: var(--font-size-xs);
  }

  .editor__desc-tab--active {
    background: rgb(var(--accent-rgb) / 0.12);
    border-color: rgb(var(--accent-rgb) / 0.5);
    color: var(--color-accent-text);
  }

  .editor__desc-preview {
    border: var(--border-subtle);
    background: var(--color-surface-925);
    padding: var(--space-3) var(--space-4);
    min-height: 8rem;
  }

  .editor__desc-empty {
    margin: 0;
    color: var(--color-text-muted);
    font-size: var(--font-size-sm);
  }

  .editor__desc-hint {
    font-size: var(--font-size-2xs);
    color: var(--color-text-muted);
  }

  label {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
    font-size: var(--font-size-xs);
    color: var(--color-text-muted);
  }

  input,
  select,
  textarea {
    padding: var(--space-2) var(--space-3);
    border-radius: var(--radius-sm);
    border: var(--border-subtle);
    background: var(--color-surface-900);
    color: var(--color-text-primary);
    font-size: var(--font-size-sm);
    font-family: inherit;
  }

  input:disabled {
    opacity: 0.5;
  }

  .editor__check {
    flex-direction: row;
    align-items: center;
    gap: var(--space-2);
    align-self: end;
    padding-bottom: var(--space-2);
  }

  .editor__check input {
    width: auto;
  }

  .editor__section summary {
    cursor: pointer;
    font-weight: 600;
    font-size: var(--font-size-sm);
    padding: var(--space-2) 0;
  }

  .editor__hint {
    margin: var(--space-1) 0 var(--space-3);
    font-size: var(--font-size-xs);
    color: var(--color-text-muted);
    max-width: 60ch;
  }

  .editor__extras {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    margin-top: var(--space-2);
  }

  .editor__extra {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    padding: var(--space-3);
    border-radius: var(--radius-md);
    border: var(--border-subtle);
  }

  .editor__remove {
    align-self: flex-end;
    font-size: var(--font-size-xs);
  }

  .editor__footer {
    display: flex;
    gap: var(--space-3);
  }
</style>
